import React, { useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { useRouterActions } from '@atlassian/react-resource-router';
import type { ViewNotFoundErrorPage$key } from '@atlassian/jira-relay/src/__generated__/ViewNotFoundErrorPage.graphql';
import imageUrl from '@atlassian/jira-error-pages/src/assets/not-found.svg';
import { ErrorPage } from '@atlassian/jira-error-pages/src/common/ui/ErrorPage.tsx';
import messages from './messages.tsx';

const styles = cssMap({
	root: {
		height: '100%',
		display: 'flex',
	},
	surface: {
		flex: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginTop: token('space.300'),
		marginRight: token('space.300'),
		marginBottom: token('space.300'),
		marginLeft: token('space.300'),
		borderRadius: token('border.radius.200'),
		backgroundColor: token('elevation.surface'),
	},
});

export type Scope = 'board' | 'project' | 'plan';

type Props = {
	defaultNavigationItem?: ViewNotFoundErrorPage$key | null;
	scope: Scope;
	viewNotFoundDescription: string;
	containerNotFoundDescription: string;
	containerNotFoundButtonMessage: string;
	containerNotFoundRedirectUrl: string;
};

const ViewNotFoundErrorPage = ({
	scope,
	defaultNavigationItem,
	viewNotFoundDescription,
	containerNotFoundDescription,
	containerNotFoundButtonMessage,
	containerNotFoundRedirectUrl,
}: Props) => {
	const { push, replace } = useRouterActions();
	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment ViewNotFoundErrorPage on JiraNavigationItem {
				url
			}
		`,
		defaultNavigationItem,
	);

	const hasDefaultViewUrl = Boolean(data?.url);
	const buttonMessage = hasDefaultViewUrl
		? formatMessage(messages.goToDefaultView)
		: containerNotFoundButtonMessage;
	const description = hasDefaultViewUrl ? viewNotFoundDescription : containerNotFoundDescription;
	const url = data?.url || containerNotFoundRedirectUrl;

	const onButtonClick = useCallback(
		() => (hasDefaultViewUrl ? push(url) : replace(url)),
		[hasDefaultViewUrl, push, replace, url],
	);

	return (
		<UFOSegment name="view-not-found-error-page">
			<ContextualAnalyticsData
				sourceType={SCREEN}
				sourceName="view-not-found-error-page"
				attributes={{
					scope,
					url,
					errorCode: '404',
					errorType: hasDefaultViewUrl ? 'view_not_found' : 'container_not_found',
				}}
			>
				<Box xcss={styles.root}>
					<Box xcss={styles.surface}>
						<ErrorPage
							header={formatMessage(messages.header)}
							description={description}
							buttonMessage={buttonMessage}
							imageUrl={imageUrl}
							errorCode="404"
							onButtonClick={onButtonClick}
							error={null}
							isFullPage={false}
						/>
					</Box>
				</Box>
				<FireScreenAnalytics />
			</ContextualAnalyticsData>
		</UFOSegment>
	);
};

export default ViewNotFoundErrorPage;
